import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, CircularProgress, IconButton, Snackbar, Paper, List, ListItem,
  ListItemText, ListItemSecondaryAction, Tooltip, Button, Stack, Chip, Tabs, Tab,
  Pagination, Divider
} from "@mui/material";
import { CheckCircleOutline, Refresh, DoneAll, OpenInNew } from "@mui/icons-material";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

function timeAgo(tsIso) {
  if (!tsIso) return "";
  const d = new Date(tsIso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "hace unos segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return d.toLocaleString();
}

const PAGE_SIZE = 10;

const Avisos = () => {
  const { usuario, token } = useAuth();
  const [data, setData] = useState({
    content: [],
    totalPages: 0,
    totalElements: 0,
    number: 0,   // 0-based
    size: PAGE_SIZE
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [tab, setTab] = useState(0); // 0=Todos, 1=No leídos
  const [page, setPage] = useState(1); // 1-based para MUI
  const sseRef = useRef(null);

  const API = process.env.REACT_APP_API_URL;
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // --- SOLO PAGINACIÓN: normalizamos snake_case/camelCase del backend
  const normalizePage = (raw) => {
    const pageable = raw?.pageable || {};
    const size =
      raw?.size ??
      pageable?.page_size ??
      PAGE_SIZE;

    return {
      content: raw?.content ?? [],
      totalPages: raw?.total_pages ?? raw?.totalPages ?? 0,
      totalElements: raw?.total_elements ?? raw?.totalElements ?? 0,
      number: raw?.number ?? 0, // 0-based
      size
    };
  };

  const fetchAvisos = async (opts = {}) => {
    if (!usuario?.codigo) return;
    try {
      setLoading(true);
      const soloNoLeidos = tab === 1;
      const res = await axios.get(
        `${API}/api/avisos/usuario/${usuario.codigo}`,
        {
          params: {
            soloNoLeidos,
            page: page - 1,          // enviamos 0-based
            size: PAGE_SIZE,
            sort: "creadoEl,DESC",
            ...opts
          },
          headers: auth
        }
      );

      const normalized = normalizePage(res.data);
      setData(normalized);
      // Sincroniza nuestra 1-based con la 0-based real del backend
      setPage(normalized.number + 1);
    } catch (e) {
      console.error(e);
      setMsg("Error al cargar avisos.");
    } finally {
      setLoading(false);
    }
  };

  const notifyBadge = () => window.dispatchEvent(new CustomEvent("avisos:refresh"));

  const marcarComoLeido = async (id) => {
    try {
      await axios.post(`${API}/api/avisos/${id}/leido`, {}, { headers: auth });
      setData(prev => ({ ...prev, content: prev.content.map(a => a.id === id ? { ...a, leido: true } : a) }));
      notifyBadge();
    } catch (e) {
      setMsg("No se pudo marcar como leído.");
    }
  };

  const marcarTodos = async () => {
    try {
      await axios.post(`${API}/api/avisos/marcar-todos-leidos`, {}, { headers: auth });
      setTab(0);
      setPage(1);
      await fetchAvisos({ page: 0 });
      notifyBadge();
    } catch (e) {
      setMsg("No se pudieron marcar todos como leídos.");
    }
  };

  // SSE (NO TOCADO). Si te da 403, puedes comentar estas dos líneas: iniciarSSE() y el bloque entero.
  /*const iniciarSSE = () => {
    if (!usuario?.codigo) return;
    try {
      sseRef.current?.close?.();
      const url = `${API.replace(/^http/, "http")}/api/avisos/stream/${usuario.codigo}`;
      const ev = new EventSource(url, { withCredentials: true });
      sseRef.current = ev;
      ev.onmessage = (evt) => {
        try {
          const nuevo = JSON.parse(evt.data);
          setData((prev) => {
            const soloNoLeidos = tab === 1;
            const visible = !soloNoLeidos || (soloNoLeidos && !nuevo.leido);
            if (!visible) return prev;

            const updated = [nuevo, ...prev.content];
            if (updated.length > prev.size) updated.pop();

            return {
              ...prev,
              content: updated,
              totalElements: (prev.totalElements ?? 0) + 1
            };
          });
        } catch {}
      };
      ev.onerror = () => {};
    } catch {}
  };*/

  useEffect(() => {
    fetchAvisos();
    //iniciarSSE(); // comenta si no usas SSE
    return () => sseRef.current?.close?.();
  }, [usuario?.codigo, tab, page]);

  const content = data.content;
  const totalPages = data.totalPages || Math.ceil((data.totalElements || 0) / (data.size || PAGE_SIZE)) || 1;
  const total = data.totalElements || 0;
  const backendPage0 = data.number || 0;
  const size = data.size || PAGE_SIZE;

  const from = total === 0 ? 0 : backendPage0 * size + 1;
  const to = backendPage0 * size + content.length;

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Mis Avisos</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<DoneAll />} onClick={marcarTodos} variant="outlined">
            Marcar todos leídos
          </Button>
          <IconButton onClick={() => fetchAvisos()}><Refresh /></IconButton>
        </Stack>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(1); }} sx={{ mb: 2 }}>
        <Tab label="Todos" />
        <Tab label="No leídos" />
      </Tabs>

      {loading ? (
        <CircularProgress />
      ) : content.length === 0 ? (
        <Typography>No tienes avisos.</Typography>
      ) : (
        <Paper elevation={2}>
          <List>
            {content.map((av) => (
              <React.Fragment key={av.id}>
                <ListItem
                  divider
                  sx={{ bgcolor: av.leido ? "grey.100" : "white", alignItems: "flex-start" }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={600}>{av.titulo}</Typography>
                        {av.estadoRelacionado && <Chip size="small" label={av.estadoRelacionado} />}
                        {av.tipo && <Chip size="small" variant="outlined" label={av.tipo} />}
                        <Typography variant="body2" color="text.secondary">
                          {timeAgo(av.creadoEl)}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <>
                        <Typography variant="body2">{av.contenido}</Typography>
                        {av.entidad && av.entidadId && (
                          <Typography variant="caption" color="text.secondary">
                            Ref: {av.entidad} #{av.entidadId}
                          </Typography>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={1}>
                      {av.urlAccion && (
                        <Tooltip title="Ir">
                          <IconButton
                            onClick={() => window.location.assign(av.urlAccion)}
                            color="inherit"
                          >
                            <OpenInNew />
                          </IconButton>
                        </Tooltip>
                      )}
                      {av.leido ? (
                        <Tooltip title="Leído">
                          <CheckCircleOutline color="success" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Marcar como leído">
                          <IconButton onClick={() => marcarComoLeido(av.id)} color="primary">
                            <CheckCircleOutline />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>

          <Divider />

          <Box p={2} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography variant="body2" color="text.secondary">
              {total > 0 ? `Mostrando ${from}–${to} de ${total}` : "Mostrando 0 de 0"}
            </Typography>

            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
            />
          </Box>
        </Paper>
      )}

      <Snackbar open={!!msg} autoHideDuration={4000} onClose={() => setMsg("")} message={msg} />
    </Box>
  );
};

export default Avisos;
